import {
  validateReportName,
  isReportNameInUse,
  extractApiErrorMessage,
} from "../utils/reportDialogUtils";
import { useLastOpenedReport } from "./useLastOpenedReport";
import * as logger from "common/logger";
import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import * as reportsV3Service from "services/reportsV3Service";

export const useReportDialog = (savedReports) => {
  const navigate = useNavigate();
  const { forgetOpened } = useLastOpenedReport();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("create");
  const [dialogReportId, setDialogReportId] = useState(null);
  const [dialogReportName, setDialogReportName] = useState("");
  const [dialogReportDescription, setDialogReportDescription] = useState("");
  const [dialogError, setDialogError] = useState("");
  const [shareError, setShareError] = useState("");
  const [isDeletingReport, setIsDeletingReport] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteReport] = reportsV3Service.useDeleteReportMutation();
  const [updateReport] = reportsV3Service.useUpdateReportMutation();
  const [
    updateReportMetadata,
    { isLoading: isUpdatingMetadata },
  ] = reportsV3Service.useUpdateReportMetadataMutation();
  const [fetchReport] = reportsV3Service.useLazyGetReportQuery();

  const { data: reportMetadata } = reportsV3Service.useGetReportMetadataQuery(
    dialogReportId,
    {
      skip: !dialogReportId || dialogMode !== "edit",
    }
  );

  const selectedReportIsReadOnly = Boolean(
    dialogReportId && reportMetadata?.readOnly
  );

  const [originalReportName, setOriginalReportName] = useState("");
  const [originalReportDescription, setOriginalReportDescription] = useState(
    ""
  );

  const handleOpenEditReportDialog = useCallback(
    (reportId) => {
      const report = (savedReports ?? []).find(
        (r) => r.internalName === reportId
      );
      const name = report?.displayName ?? "";
      const desc = report?.description ?? "";
      setDialogMode("edit");
      setDialogReportId(reportId);
      setDialogReportName(name);
      setDialogReportDescription(desc);
      setOriginalReportName(name);
      setOriginalReportDescription(desc);
      setDialogError("");
      setShareError("");
      setDialogOpen(true);
    },
    [savedReports]
  );

  const handleEditReport = useCallback(() => {
    if (!dialogReportId) return;
    setDialogOpen(false);
    navigate(`/report-builder/${dialogReportId}/edit`);
  }, [dialogReportId, navigate]);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setTimeout(() => {
      setDialogReportId(null);
      setDialogReportName("");
      setDialogReportDescription("");
      setDialogError("");
      setShareError("");
    }, 200);
  }, []);

  const handleDialogSave = useCallback(async () => {
    const {
      valid,
      trimmed: trimmedName,
      error: nameError,
    } = validateReportName(dialogReportName);
    if (!valid) {
      setDialogError(nameError);
      return;
    }
    const excludeId = dialogMode === "edit" ? dialogReportId : null;
    if (isReportNameInUse(trimmedName, savedReports, excludeId)) {
      setDialogError(
        "A report with this name already exists. Choose another name."
      );
      return;
    }
    if (dialogMode === "create") {
      navigate("/report-builder/new", {
        state: {
          reportName: trimmedName,
          reportDescription: dialogReportDescription.trim(),
        },
      });
      handleDialogClose();
    } else if (dialogMode === "edit" && dialogReportId) {
      setIsSaving(true);
      try {
        const { data: fullReport } = await fetchReport(dialogReportId);
        if (!fullReport) {
          setDialogError("Failed to load report details for saving.");
          return;
        }
        let reportData;
        if (fullReport.type === "MULTI_TABLE_REPORT") {
          reportData = {
            type: fullReport.type,
            displayName: trimmedName,
            description: dialogReportDescription.trim() || "",
            reports: fullReport.reports,
          };
        } else {
          if (!fullReport.specification) {
            setDialogError("Failed to load report details for saving.");
            return;
          }
          reportData = {
            type: fullReport.type ?? "SINGLE_TABLE_REPORT",
            displayName: trimmedName,
            description: dialogReportDescription.trim() || "",
            specification: fullReport.specification,
          };
        }
        await updateReport({
          reportId: dialogReportId,
          reportData,
        }).unwrap();
        handleDialogClose();
      } catch (error) {
        logger.error("[useReportDialog] Failed to update report:", error);
        setDialogError(
          extractApiErrorMessage(
            error,
            "Failed to update report. Please try again."
          )
        );
      } finally {
        setIsSaving(false);
      }
    }
  }, [
    dialogReportName,
    dialogReportDescription,
    dialogMode,
    dialogReportId,
    savedReports,
    navigate,
    fetchReport,
    updateReport,
    handleDialogClose,
  ]);

  const handleDeleteReport = useCallback(async () => {
    const reportIdToDelete = dialogReportId;
    if (!reportIdToDelete) return;
    setIsDeletingReport(true);
    try {
      await deleteReport(reportIdToDelete).unwrap();

      handleDialogClose();

      // Back to Report Builder's landing page rather than into another report,
      // so nothing runs until the user chooses.
      forgetOpened(reportIdToDelete);
      navigate("/report-builder");
    } catch (error) {
      logger.error("[useReportDialog] Failed to delete report:", error);
      setDialogError(
        extractApiErrorMessage(
          error,
          "Failed to delete report. Please try again."
        )
      );
    } finally {
      setIsDeletingReport(false);
    }
  }, [dialogReportId, deleteReport, navigate, handleDialogClose, forgetOpened]);

  // Resolves to true when the sharing settings were saved, so the dialog can
  // put its checkboxes back when they were not.
  const handleShareSettingsChange = useCallback(
    async (settings) => {
      if (!dialogReportId) return false;
      setShareError("");
      try {
        await updateReportMetadata({
          reportId: dialogReportId,
          metadata: settings,
        }).unwrap();
        return true;
      } catch (error) {
        logger.error(
          "[useReportDialog] Failed to update report metadata:",
          error
        );
        setShareError(extractApiErrorMessage(error, "Please try again."));
        return false;
      }
    },
    [dialogReportId, updateReportMetadata]
  );

  const clearDialogError = useCallback(() => {
    setDialogError("");
  }, []);

  const clearShareError = useCallback(() => {
    setShareError("");
  }, []);

  return {
    dialogOpen,
    dialogMode,
    dialogReportId,
    dialogReportName,
    dialogReportDescription,
    dialogError,
    shareError,
    originalReportName,
    originalReportDescription,
    isDeletingReport,
    isUpdatingReport: isSaving,
    isUpdatingMetadata,
    selectedReportIsReadOnly,
    reportMetadata: reportMetadata ?? null,
    handleOpenEditReportDialog,
    handleEditReport,
    handleDialogClose,
    handleDialogSave,
    handleDeleteReport,
    handleShareSettingsChange,
    setDialogReportName,
    setDialogReportDescription,
    clearDialogError,
    clearShareError,
  };
};
